import {
  Box,
  Button,
  IconButton,
  Popover,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { tokens } from '../../global/theme/tokens';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FilterGroup from './filter/FilterGroup';
import OrderGroup from './filter/OrderGroup';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';

/*
If the filter should persist for the session a cookieKey needs to be included in the filter.
It also must use the url stored at in the cookie for the initial data load, otherwise the filters are not applied.
*/

const defaultOptions = {
  filter: {
    label: 'Filter',
    items: [
      {
        label: 'No Invoices',
        value: 'assignment[doesnothave][]=invoices',
        active: false,
      },
      {
        label: 'No Receipts',
        value: 'assignment[doesnothave][]=receipts',
        active: false,
      },
    ],
  },
  order: {
    label: 'Order',
    items: [
      {
        label: 'By Booking Date',
        value: 'bookingDate',
        active: false,
      },
      {
        label: 'By Amount',
        value: 'amount',
        active: false,
      },
    ],
  },
};

const ExoAPIFilter = ({
  className,
  onChange,
  filters,
  currentFilters,
  Icon = FilterAltIcon,
  label,
  noClear,
  smallIcon = false,
  buttonClassName,
  buttonSx,
  isActive,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const cookies = new Cookies();

  // item handeling
  const [activeFilters, setActiveFilters] = useState([]);
  const [activeOrder, setActiveOrder] = useState({});

  function handleFilterChange(updatedFilterItem) {
    //console.log("ORDER", activeOrder);

    const itemIndexInActiveFilters = activeFilters.findIndex(
      (item) => item.key === updatedFilterItem.key
    );
    if (itemIndexInActiveFilters !== -1) {
      //item exists and needs to be updated
      var newFilterItems = [...activeFilters];

      newFilterItems[itemIndexInActiveFilters] = updatedFilterItem;
      setActiveFilters(newFilterItems);
    } else {
      //item does not exsist. needs to be added
      setActiveFilters([...activeFilters, { ...updatedFilterItem }]);
    }
  }

  function handleOrderChange(orderParam, orderDir) {
    setActiveOrder({
      orderParam: orderParam,
      orderDir: orderDir ? 'asc' : 'desc',
    });
  }

  function convertToString() {
    var string = '';
    activeFilters.forEach((filter) => {
      if (filter.active) string += `${filter.value}&`;
    });
    if (activeOrder.orderParam)
      string += `sortOrder=${activeOrder.orderDir}&sortBy=${activeOrder.orderParam}&`;
    return string;
  }

  function handleReset() {
    if (filters.cookieKey) {
      cookies.remove(filters.cookieKey, { path: '/' });
      sessionStorage.removeItem(filters.cookieKey);
    }
    onChange({});
    handlePopoverClose();
  }

  function handleSubmit() {
    if (filters.cookieKey) {
      cookies.set(filters.cookieKey, convertToString());
      sessionStorage.setItem(filters.cookieKey, JSON.stringify(activeFilters));
    }

    onChange({
      filters: activeFilters,
      order: activeOrder,
      string: convertToString(),
    });
    handlePopoverClose();
  }

  // popover
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setActiveFilters([]);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const isFilterActive = Boolean(
    isActive ||
      currentFilters?.string ||
      currentFilters?.filters?.some((f) => f.active) ||
      currentFilters?.order?.orderParam
  );
  const activeSx = isFilterActive
    ? {
        backgroundColor: colors.info + '30',
        borderColor: colors.info + '80',
        color: colors.info,
        '&:hover': {
          backgroundColor: colors.info + '60',
        },
      }
    : {};

  // load current filter & order
  useEffect(() => {
    if (currentFilters.filters) setActiveFilters(currentFilters.filters);
    if (currentFilters.order) setActiveOrder(currentFilters.order);
    if (!currentFilters.filters && !currentFilters.order && filters.cookieKey) {
      const cookieValue = cookies.get(filters.cookieKey);
      const sessionStorageValue = JSON.parse(
        sessionStorage.getItem(filters.cookieKey)
      );
      if (cookieValue && sessionStorageValue) {
        setActiveFilters(sessionStorageValue);
        return;
      } else if (cookieValue && !sessionStorageValue) {
        sessionStorage.removeItem(filters.cookieKey);
      }
    }
    if (!currentFilters.filters && !currentFilters.order) {
      if (filters.filter && filters.filter.items) {
        var newActiveFilters = [];
        filters.filter.items.forEach((filter) => {
          if (filter.active) newActiveFilters.push(filter);
        });
        setActiveFilters(newActiveFilters);
      }
      if (filters.order && filters.order.items.length > 0) {
        filters.order.items.forEach((order) => {
          if (order.active)
            setActiveOrder({ orderParam: order.value, orderDir: 'desc' });
        });
      }
    }
  }, [anchorEl]);

  return (
    <Box className={'flex flex-row gap-1' + ' ' + className}>
      <Tooltip title="Filter">
        {label ? (
          <Button
            className="flex flex-row gap-2 "
            onClick={handlePopoverOpen}
            sx={activeSx}
          >
            {label} {Icon && <Icon />}
          </Button>
        ) : (
          <IconButton
            onClick={handlePopoverOpen}
            size={smallIcon ? 'small' : 'medium'}
            className={buttonClassName}
            sx={{ ...buttonSx, ...activeSx }}
          >
            <Icon />
          </IconButton>
        )}
      </Tooltip>
      <Popover
        id="simple-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box className="flex flex-col p-4 gap-4 relative w-[300px]">
          {!noClear && (
            <IconButton
              onClick={handleReset}
              className="top-2 right-2"
              sx={{
                position: 'absolute',
              }}
            >
              <FilterAltOffIcon />
            </IconButton>
          )}
          {filters.filter && (
            <Box>
              <FilterGroup
                activeFilters={activeFilters}
                groupKey={'filter'}
                group={filters.filter}
                onChange={handleFilterChange}
              />
            </Box>
          )}
          {filters.order && (
            <OrderGroup
              activeOrder={activeOrder}
              groupKey={'order'}
              group={filters.order}
              onChange={handleOrderChange}
            />
          )}
          <Button
            className="w-full"
            variant="contained"
            color="success"
            onClick={handleSubmit}
          >
            {t('Update')}
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default ExoAPIFilter;
